import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import BrandHeader from '../../../components/common/BrandHeader';
import PageLoader from '../../../components/common/PageLoader';
import DetailRow from '../../../components/common/DetailRow';
import NextSteps from '../../../components/common/NextSteps';
import Note from '../../../components/common/Note';
import RescheduleEntry from '../../../components/common/RescheduleEntry';
import OutlineButton from '../../../components/common/OutlineButton';
import ResultHero from '../../../components/common/ResultHero';
import StepBar from '../../../components/common/StepBar';
import {
  Card,
  CardBody,
  Divider,
  HelpText,
  PageShell,
  SectionTitle,
} from '../../../components/common/Card';
import {
  DocumentType,
  IssuerMeta,
  IssuerName,
  LineItem,
  LineItemAmount,
  LineItemDescription,
  LineItemDetail,
  MetaGrid,
  MetaItem,
  MetaLabel,
  MetaValue,
} from '../../../components/common/DocumentCard';
import {
  BUSINESS_ABN,
  BUSINESS_NAME,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  SERVICE_DESCRIPTION,
} from '../../../constants/business';
import { PAYMENT_NEXT_STEPS } from '../../../constants/nextSteps';
import {
  PAYMENT_STATUS,
  PAYMENT_TYPE,
  PRE_APPT_COUNTER_PREFIX,
  PRE_APPT_STEPS,
  PRE_APPT_STEP_NUMBER,
} from '../../../constants/preApptConstants';
import { STATUS } from '../../../constants';
import {
  formatAmountFromCents,
  formatAppointmentDate,
  formatAppointmentDateTime,
  formatAppointmentTime,
  formatAppointmentTypeLabel,
  formatPaymentTimestamp,
} from '../../../utils/appointmentDisplayUtils';
import {
  buildGoogleCalendarUrl,
  generateCalendarEvent,
} from '../../../utils/calendarEventUtils';
import {
  fetchVerifiedWorkflowById,
  isWorkflowVerified,
} from '../../../slices/workflow';
import { ROUTE_PREFIX_PRE_APPT } from '../../../constants/routes';
import { getPatientAccessToken } from '../../../utils/patientAccessStorage';
import { getContactDetails } from '../../../utils/contactDetails';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPT_COUNT = 8;

const PaymentFinishPage = () => {
  const dispatch = useDispatch();
  const { workflowId } = useParams();
  const workflowState = useSelector(({ workflow }) => workflow);
  const { data: workflow, status: workflowFetchingStatus } = workflowState;
  const isVerified = isWorkflowVerified(workflowState, workflowId);
  const [pollAttemptCount, setPollAttemptCount] = useState(0);
  const hasAccessToken = Boolean(getPatientAccessToken(workflowId));

  useEffect(() => {
    if (!isVerified && hasAccessToken) {
      dispatch(fetchVerifiedWorkflowById(workflowId));
    }
  }, [dispatch, workflowId, isVerified, hasAccessToken]);

  const { appointment, client, payment, specialist } = workflow;
  const hasWorkflowLoaded = isVerified && !isEmpty(workflow);
  const isPaid = payment?.currentStatus?.status === PAYMENT_STATUS.PAID;
  const isManualPayment = payment?.paymentInfo?.type === PAYMENT_TYPE.MANUAL;
  const isAwaitingPayment = hasWorkflowLoaded && !isPaid && !isManualPayment;
  const hasSettledPaymentStatus = Boolean(payment?.currentStatus?.status);
  const hasPollingFinished = pollAttemptCount >= MAX_POLL_ATTEMPT_COUNT;
  const isPollingForPayment =
    isAwaitingPayment && !hasSettledPaymentStatus && !hasPollingFinished;
  const hasWorkflowFailed =
    !hasWorkflowLoaded && workflowFetchingStatus === STATUS.FAILURE;

  const handleRetry = () => dispatch(fetchVerifiedWorkflowById(workflowId));

  useEffect(() => {
    if (!isPollingForPayment) return undefined;

    const pollTimer = setTimeout(() => {
      dispatch(fetchVerifiedWorkflowById(workflowId));
      setPollAttemptCount((currentCount) => currentCount + 1);
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(pollTimer);
  }, [dispatch, workflowId, isPollingForPayment, pollAttemptCount]);

  const calendarEvent = useMemo(
    () => generateCalendarEvent(workflow),
    [workflow]
  );
  const calendarUrl = buildGoogleCalendarUrl(calendarEvent);

  const clientName = `${client?.firstName || ''} ${
    client?.lastName || ''
  }`.trim();
  const clinicianName = specialist
    ? `Dr. ${specialist.firstName} ${specialist.lastName}`
    : '';
  const clinicianDetail = specialist?.speciality
    ? `${clinicianName} · ${specialist.speciality}`
    : clinicianName;
  const appointmentDate = formatAppointmentDate(appointment?.startDateTime);
  const appointmentTime = formatAppointmentTime(appointment?.startDateTime);
  const appointmentDateTime = formatAppointmentDateTime(
    appointment?.startDateTime
  );
  const appointmentTypeLabel = formatAppointmentTypeLabel(
    appointment?.appointmentType
  );
  const amountPaid = formatAmountFromCents(payment?.currentStatus?.amount);
  const paidAt = formatPaymentTimestamp(payment?.currentStatus?.createdDate);
  const confirmedSubtitle = clinicianName
    ? `Your appointment with ${clinicianName} is confirmed.`
    : 'Your appointment is confirmed.';

  const nextStepsCard = (
    <Card>
      <CardBody>
        <SectionTitle>What happens next</SectionTitle>
        <NextSteps steps={PAYMENT_NEXT_STEPS} />
      </CardBody>
    </Card>
  );

  const calendarCard = calendarUrl ? (
    <Card>
      <CardBody>
        <OutlineButton
          href={calendarUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Add to calendar
        </OutlineButton>
      </CardBody>
    </Card>
  ) : null;

  // Same contact as the reschedule entry above it: the ADHD team for ADHD workflows.
  const changeContact = getContactDetails({
    isAdhdPathway: workflow?.isAdhdPathway,
  });
  const contactHelpText = (
    <HelpText>
      Need to change or cancel? Call {changeContact.phone} or email{' '}
      <a href={`mailto:${changeContact.email}`}>{changeContact.email}</a>.
      Cancelling within <b>2 business days</b> may incur a fee ($150).
    </HelpText>
  );

  const hasAppointmentDetail = Boolean(
    clinicianDetail ||
      appointmentDate ||
      appointmentTime ||
      appointmentTypeLabel
  );

  const appointmentCard = hasAppointmentDetail ? (
    <Card>
      <CardBody>
        <SectionTitle>Your appointment</SectionTitle>
        {clinicianDetail && (
          <DetailRow label="Clinician" value={clinicianDetail} />
        )}
        {appointmentDate && <DetailRow label="Date" value={appointmentDate} />}
        {appointmentTime && <DetailRow label="Time" value={appointmentTime} />}
        {appointmentTypeLabel && (
          <DetailRow label="Type" value={appointmentTypeLabel} />
        )}
      </CardBody>
    </Card>
  ) : null;

  const receiptContent = (
    <>
      <ResultHero
        tone="success"
        title="Payment received"
        subtitle={confirmedSubtitle}
      />

      <Card>
        <CardBody>
          <DocumentType>Receipt</DocumentType>
          <IssuerName>{BUSINESS_NAME}</IssuerName>
          <IssuerMeta>{BUSINESS_ABN}</IssuerMeta>
          <MetaGrid>
            {clientName && (
              <MetaItem>
                <MetaLabel>Paid by</MetaLabel>
                <MetaValue>{clientName}</MetaValue>
              </MetaItem>
            )}
            {paidAt && (
              <MetaItem>
                <MetaLabel>Paid on</MetaLabel>
                <MetaValue>{paidAt}</MetaValue>
              </MetaItem>
            )}
            <MetaItem>
              <MetaLabel>Reference</MetaLabel>
              <MetaValue>{workflowId}</MetaValue>
            </MetaItem>
          </MetaGrid>
        </CardBody>

        <Divider />

        <CardBody>
          <SectionTitle>Service details</SectionTitle>
          <LineItem>
            <div>
              <LineItemDescription>{SERVICE_DESCRIPTION}</LineItemDescription>
              {clinicianDetail && (
                <LineItemDetail>{clinicianDetail}</LineItemDetail>
              )}
              {appointmentDateTime && (
                <LineItemDetail>{appointmentDateTime}</LineItemDetail>
              )}
            </div>
            <LineItemAmount>{amountPaid}</LineItemAmount>
          </LineItem>
          <DetailRow
            label="Total paid"
            value={amountPaid}
            currency="AUD"
            emphasised
          />
        </CardBody>
      </Card>

      {nextStepsCard}
      {calendarCard}
      <RescheduleEntry workflow={workflow} />
      {contactHelpText}
    </>
  );

  const manualPaymentContent = (
    <>
      <ResultHero
        tone="success"
        title="Your appointment is confirmed"
        subtitle={confirmedSubtitle}
      />

      <Note>
        <b>Payment still to be arranged.</b> Our team will contact you to
        arrange payment for this appointment.
      </Note>

      {appointmentCard}
      {nextStepsCard}
      {calendarCard}
      <RescheduleEntry workflow={workflow} />
      {contactHelpText}
    </>
  );

  const awaitingPaymentContent = (
    <>
      {isPollingForPayment ? (
        <ResultHero
          tone="pending"
          title="We’re confirming your payment"
          subtitle="Please keep this page open. Your receipt appears here as soon as our payment provider confirms the transaction."
        />
      ) : (
        <ResultHero
          tone="warning"
          title="We could not confirm your payment yet"
        />
      )}

      {isPollingForPayment ? (
        <Note>
          Confirmation usually arrives within a few seconds. You do not need to
          pay again.
        </Note>
      ) : (
        <Note tone="warning">
          <b>We have not received a payment confirmation.</b> If your card was
          charged, please do not pay again. Call {CONTACT_PHONE} or email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will
          check it for you.
        </Note>
      )}

      {appointmentCard}
      {contactHelpText}
    </>
  );

  const loadFailureContent = (
    <>
      <ResultHero
        tone="warning"
        title="We can’t load your appointment details"
        subtitle="If you have just paid, your payment has still gone through. Please do not pay again."
      />

      <Note tone="warning">
        <b>Something went wrong on our side.</b> Try again in a moment. If this
        keeps happening, call {CONTACT_PHONE} or email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will
        confirm your appointment and payment for you.
      </Note>

      <Card>
        <CardBody>
          <OutlineButton onClick={handleRetry}>Try again</OutlineButton>
        </CardBody>
      </Card>
    </>
  );

  const stepNumber = isAwaitingPayment
    ? PRE_APPT_STEP_NUMBER.PAYMENT
    : PRE_APPT_STEP_NUMBER.COMPLETE;

  if (!hasAccessToken) {
    return <Redirect to={`${ROUTE_PREFIX_PRE_APPT}/${workflowId}`} />;
  }

  if (!hasWorkflowLoaded && !hasWorkflowFailed) {
    return <PageLoader />;
  }

  return (
    <>
      {hasWorkflowFailed && (
        <>
          <BrandHeader />
          <PageShell>{loadFailureContent}</PageShell>
        </>
      )}

      {hasWorkflowLoaded && (
        <>
          <BrandHeader />
          <PageShell>
            <StepBar
              steps={PRE_APPT_STEPS}
              currentStepNumber={stepNumber}
              counterPrefix={PRE_APPT_COUNTER_PREFIX}
              counterLabel={isAwaitingPayment ? undefined : 'Complete'}
            />

            {isPaid && receiptContent}
            {!isPaid && isManualPayment && manualPaymentContent}
            {isAwaitingPayment && awaitingPaymentContent}
          </PageShell>
        </>
      )}
    </>
  );
};

export default PaymentFinishPage;
